import axios from 'axios';
import { toast } from 'sonner';
import { offlineDb } from './db';

/* Motor de sincronización: sube capturas/figuras guardadas sin conexión cuando vuelve la red */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REQUEST_TIMEOUT = 10000;
const UPLOAD_TIMEOUT = 60000;
const RETRY_INTERVAL = 45000;

// true si el error amerita guardar localmente y reintentar después (sin red, timeout o error de servidor)
export const shouldQueue = (error) =>
  !navigator.onLine || !error?.response || error.response.status >= 500;

export const postWithTimeout = (url, data) => axios.post(url, data, { timeout: REQUEST_TIMEOUT });

class SyncManager extends EventTarget {
  isOnline = navigator.onLine;
  status = 'idle'; // idle | syncing | error
  pending = { concepts: 0, captures: 0, figures: 0, total: 0 };
  lastError = null;
  lastSyncAt = null;
  _running = false;
  _initialized = false;

  init() {
    if (this._initialized) return;
    this._initialized = true;
    window.addEventListener('online', () => {
      this.isOnline = true;
      this._emit();
      setTimeout(() => this.sync(), 1500);
    });
    window.addEventListener('offline', () => {
      this.isOnline = false;
      this._emit();
    });
    setInterval(() => {
      if (this.pending.total > 0) this.sync();
    }, RETRY_INTERVAL);
    this.refreshPending().then(() => this.sync());
  }

  getState() {
    return { isOnline: this.isOnline, status: this.status, pending: this.pending, lastError: this.lastError, lastSyncAt: this.lastSyncAt };
  }

  _emit() {
    this.dispatchEvent(new Event('change'));
  }

  // Avisa a la UI que la lista de conceptos (locales o sincronizados) cambió
  notifyConceptsChanged() {
    this.dispatchEvent(new Event('concepts-changed'));
  }

  async refreshPending() {
    try {
      this.pending = await offlineDb.countPending();
    } catch (e) {
      console.error('Offline: error contando pendientes', e);
    }
    this._emit();
  }

  async sync() {
    if (this._running || !navigator.onLine) return;
    await this.refreshPending();
    if (this.pending.total === 0) return;

    this._running = true;
    this.status = 'syncing';
    this.lastError = null;
    this._emit();

    let conceptsSynced = 0;
    let capturesSynced = 0;
    let figuresSynced = 0;
    let stoppedByNetwork = false;

    try {
      // 0. Conceptos pendientes PRIMERO: capturas y figuras pueden referenciarlos
      for (const con of await offlineDb.getAllConcepts()) {
        try {
          await axios.post(`${API}/concepts`, con.payload, { timeout: REQUEST_TIMEOUT });
          await offlineDb.deleteConcept(con.id);
          conceptsSynced++;
        } catch (err) {
          if (shouldQueue(err)) { stoppedByNetwork = true; break; }
          await offlineDb.putConcept({ ...con, lastError: err.response?.data?.detail || err.message });
        }
        await this.refreshPending();
      }
      if (conceptsSynced > 0) this.notifyConceptsChanged();

      // 1. Capturas pendientes (en orden de captura real); esperan si su concepto sigue local
      if (!stoppedByNetwork) {
        for (const cap of await offlineDb.getAllCaptures()) {
          if (cap.synced) continue;
          if (await offlineDb.getConcept(cap.payload?.concept_id)) continue;
          try {
            await axios.post(`${API}/captures`, cap.payload, { timeout: UPLOAD_TIMEOUT });
            await offlineDb.putCapture({ ...cap, synced: true, payload: null, lastError: null });
            capturesSynced++;
          } catch (err) {
            if (shouldQueue(err)) { stoppedByNetwork = true; break; }
            await offlineDb.putCapture({ ...cap, lastError: err.response?.data?.detail || err.message });
          }
          await this.refreshPending();
        }
      }

      // 2. Figuras pendientes (solo si su captura y su concepto ya existen en el servidor)
      if (!stoppedByNetwork) {
        for (const fig of await offlineDb.getAllFigures()) {
          const cap = await offlineDb.getCapture(fig.capture_id);
          if (cap && !cap.synced) continue;
          if (await offlineDb.getConcept(fig.payload?.concept_id)) continue;
          try {
            await axios.post(`${API}/figures`, fig.payload, { timeout: UPLOAD_TIMEOUT });
            await offlineDb.deleteFigure(fig.id);
            figuresSynced++;
          } catch (err) {
            if (shouldQueue(err)) { stoppedByNetwork = true; break; }
            await offlineDb.putFigure({ ...fig, lastError: err.response?.data?.detail || err.message });
          }
          await this.refreshPending();
        }
      }

      // 3. Limpiar capturas ya confirmadas sin figuras pendientes
      for (const cap of await offlineDb.getAllCaptures()) {
        if (cap.synced && (await offlineDb.getFiguresForCapture(cap.id)).length === 0) {
          await offlineDb.deleteCapture(cap.id);
        }
      }
    } catch (err) {
      console.error('Offline: error en sincronización', err);
      this.lastError = err.message;
    } finally {
      this._running = false;
      await this.refreshPending();
      this.lastSyncAt = new Date();
      this.status = stoppedByNetwork || this.pending.total > 0 ? 'error' : 'idle';
      if (stoppedByNetwork) this.lastError = 'Sin conexión estable. Se reintentará automáticamente.';
      this._emit();
      if (conceptsSynced + capturesSynced + figuresSynced > 0) {
        const parts = [];
        if (conceptsSynced) parts.push(`${conceptsSynced} concepto${conceptsSynced !== 1 ? 's' : ''}`);
        parts.push(`${capturesSynced} foto${capturesSynced !== 1 ? 's' : ''}`);
        parts.push(`${figuresSynced} figura${figuresSynced !== 1 ? 's' : ''}`);
        toast.success(`Sincronizado: ${parts.join(', ')}`);
      }
    }
  }
}

export const syncManager = new SyncManager();
